/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @angular-eslint/prefer-standalone */
import {
  Component,
  EventEmitter,
  input,
  Input,
  OnInit,
  Output,
  OnDestroy,
  AfterViewChecked,
  effect,
  signal
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { map, filter } from 'rxjs/operators';
import {
  IVendorLocation,
  IVendorOnboardingResult,
  IVendorOnboardingSave
} from 'src/app/store/vendor/vendor.model';
import { LoggerDatadogService, UserRole } from 'sustainment-component';
import {
  ICapabilitytProcess,
  ICapability,
  SupplierListModel
} from 'sustainment-component';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as ManufacturingHelper from 'sustainment-component';
import { OnboardingAPI } from 'src/app/api/onboarding.api';
import { Title } from '@angular/platform-browser';
import { regex } from 'sustainment-component';
import { UserAccountQuery } from 'src/app/store/userAccount/user-account.query';
import { StateQuery } from 'src/app/store/state/state.query';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

export enum RegistrationSteps {
  CompanyDetails,
  CompanySuggestions,
  CompanyTypeRegistration,
  UserProblems,
  WelcomeScreen
}

export enum RegistrationMode {
  Invitation,
  Normal,
  Claim
}

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
  standalone: false
})
export class RegistrationComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @Input() public set vendorInfo(value: Partial<IVendorLocation>) {
    if (value && Object.keys(value).length > 0) {
      this._vendorInfo = value;

      if (this.form) {
        setTimeout(() => this.setForm(), 0);
      }
    }
  }

  public get vendorInfo(): Partial<IVendorLocation> {
    return this._vendorInfo;
  }

  private _vendorInfo: Partial<IVendorLocation>;

  @Input() public processes: ICapabilitytProcess[];
  @Input() public industries: ICapability[];
  public userRolesSignal = signal<UserRole[]>([]);
  @Input() public set userRolesData(value: UserRole[]) {
    if (value && value.length > 0 && value !== this.userRolesSignal()) {
      this.userRolesSignal.set(value);
      this._userRolesSubject.next(value);
      this.userRolesLoading = false;
    }
  }

  public get userRolesData(): UserRole[] {
    return this.userRolesSignal();
  }

  public userRoles = input<UserRole[]>([]);

  @Output() public logout = new EventEmitter<void>();
  @Output() public patchVendor = new EventEmitter<{
    vendor: IVendorOnboardingResult;
    roles: { id: number; text: string }[];
    problems: { id: number; text: string }[];
  }>();
  @Output() public vendorUpdated = new EventEmitter<IVendorOnboardingSave>();
  @Output() public requestedToJoin = new EventEmitter<SupplierListModel>();

  public vendorSelected: SupplierListModel | null;
  public form = new FormGroup({
    name: new FormControl('', [Validators.maxLength(300), Validators.required]),
    address: new FormControl('', [
      Validators.maxLength(200),
      Validators.required
    ]),
    address2: new FormControl('', [Validators.maxLength(200)]),
    city: new FormControl('', [Validators.maxLength(200), Validators.required]),
    state: new FormControl(''),
    zip: new FormControl(''),
    county: new FormControl(''),
    country: new FormControl('US'), // Default to US
    latitude: new FormControl<number | null>(null),
    longitude: new FormControl<number | null>(null),
    website: new FormControl('', [
      Validators.pattern(regex.websiteUrlValidatorPattern)
    ])
  });
  public disableContinue = true;
  public loadCompaniesSuggestion = false;

  public suggestedVendors: SupplierListModel[] = [];

  public steps = RegistrationSteps;
  public currentStep = this.steps.CompanyDetails;
  public isInternationalCompany: boolean;
  public internationalAcknowledgmentAccepted: boolean;
  public selectedProblems: ManufacturingHelper.UserProblem[] = [];

  public roles: { id: number; text: string }[] = [];
  public problems: { id: number; text: string }[];
  public userRolesLoading = true;
  public userRoles$: Observable<UserRole[]>;

  @Input() public set refCode(value: string | null) {
    this._refCode = value;
    if (value && this._registrationMode !== RegistrationMode.Invitation) {
      this._registrationMode = RegistrationMode.Invitation;
      if (this.currentStep === this.steps.CompanyDetails) {
        this.currentStep = this.steps.CompanyTypeRegistration;
      }
    }
  }

  public get refCode(): string | null {
    return this._refCode;
  }

  private _refCode: string | null = null;

  private _rolesSubject = new BehaviorSubject<{ id: number; text: string }[]>(
    []
  );
  private _userRolesSubject = new BehaviorSubject<UserRole[]>([]);
  private _subscription = new Subscription();
  private _rolesInitialized = false;
  private _registrationMode: RegistrationMode = RegistrationMode.Normal;

  public constructor(
    public loggerDatadogService: LoggerDatadogService,
    private _stateQuery: StateQuery,
    private onboardingApi: OnboardingAPI,
    private titleService: Title,
    private _userAccountQuery: UserAccountQuery
  ) {
    this.titleService.setTitle('Set up your account - Sustainment Portal');

    effect(() => {
      const userRoles = this.userRolesSignal();
      if (userRoles && userRoles.length > 0) {
        if (this.currentStep === this.steps.CompanyTypeRegistration) {
          this.calculateSelectedProblems();
        }
      }
    });
  }

  private get _localStorageKey(): string {
    const userEmail = this._userAccountQuery.email || '';
    const emailPart = userEmail ? `-${userEmail}` : '';
    return `registrationFormData${emailPart}`;
  }

  public ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const urlRefCode = urlParams.get('ref');

    if (urlRefCode && !this._refCode) {
      this.refCode = urlRefCode;
    }

    if (this.refCode) {
      this._registrationMode = RegistrationMode.Invitation;
    } else if (this._registrationMode !== RegistrationMode.Invitation) {
      this._registrationMode = RegistrationMode.Normal;
    }

    const savedData = localStorage.getItem(this._localStorageKey);
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      this.vendorInfo = parsedData.vendorInfo || {};

      if (this._registrationMode === RegistrationMode.Invitation) {
        this.currentStep = this.steps.CompanyTypeRegistration;
      } else {
        this.currentStep = parsedData.currentStep || this.steps.CompanyDetails;
      }

      this.roles = parsedData.roles || [];
      this.problems = parsedData.problems;
      this._rolesSubject.next(this.roles);

      if (parsedData.vendorSelected) {
        this.vendorSelected = parsedData.vendorSelected;
        this._registrationMode = RegistrationMode.Claim;
      }

      if (parsedData.formValues) {
        this.form.patchValue(parsedData.formValues);
      }

      if (
        this.currentStep === this.steps.CompanySuggestions &&
        this.form.value.name
      ) {
        this.loadSuggestedCompanies();
      }
    }

    if (!this.vendorInfo?.name && !this.refCode) {
      this.vendorInfo = {};
    }

    if (this.vendorInfo && Object.keys(this.vendorInfo).length > 0) {
      this.setForm();
    }

    this.userRoles$ = this._userRolesSubject
      .asObservable()
      .pipe(filter((roles) => !!roles && roles.length > 0));

    this._subscription.add(
      this.userRoles$.subscribe((roles) => {
        this.userRolesLoading = false;
        this.userRolesData = roles;
        this.calculateSelectedProblems();
      })
    );

    const initialRoles = this.getUserRoles();
    if (initialRoles && initialRoles.length > 0) {
      this._userRolesSubject.next(initialRoles);
      this.userRolesLoading = false;
    }

    this._subscription.add(
      this.form.valueChanges.subscribe(() => {
        if (this.form.valid || this.form.dirty) {
          this.saveFormDataToLocalStorage();
        }
      })
    );

    this.setupSelectedProblemsReaction();
  }

  public ngAfterViewChecked(): void {
    if (
      !this._rolesInitialized &&
      !this.userRolesLoading &&
      this.getUserRoles().length > 0
    ) {
      this._rolesInitialized = true;
      this.calculateSelectedProblems();

      if (
        this.currentStep === this.steps.CompanyTypeRegistration &&
        this.selectedProblems.length
      ) {
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.currentStep = this.steps.UserProblems;
          this.saveFormDataToLocalStorage();
        });
      }
    }
  }

  private setupSelectedProblemsReaction(): void {
    this._subscription.add(
      this._rolesSubject.subscribe(() => {
        this.calculateSelectedProblems();
      })
    );
  }

  private calculateSelectedProblems(): void {
    const userRoles = this.getUserRoles();

    if (
      userRoles &&
      userRoles.length > 0 &&
      this.roles &&
      this.roles.length > 0
    ) {
      this.updateSelectedProblems(userRoles);
    }
  }

  private updateSelectedProblems(userRoles: UserRole[]): void {
    if (!userRoles?.length || !this.roles?.length) return;

    const selectedRoleIds = this.roles.map((e) => e.id);

    this.selectedProblems = userRoles
      .filter((role: UserRole) => selectedRoleIds.includes(role.userRoleId))
      .reduce((acc: ManufacturingHelper.UserProblem[], role: UserRole) => {
        role.problems.forEach((problem: ManufacturingHelper.UserProblem) => {
          if (!acc.some((p) => p.userProblemId === problem.userProblemId)) {
            acc.push(problem);
          }
        });
        return acc;
      }, [])
      .sort(
        (
          a: ManufacturingHelper.UserProblem,
          b: ManufacturingHelper.UserProblem
        ) => a.userProblemId - b.userProblemId
      );
  }

  public setForm(): void {
    if (!this.vendorInfo) {
      return;
    }

    const shouldBypassToRoles = this._registrationMode === RegistrationMode.Invitation;

    if (typeof this.vendorInfo.primaryAddress === 'string') {
      const addressParts = this.vendorInfo.primaryAddress.split(',');
      if (addressParts.length >= 3) {
        const street = addressParts[0].trim();
        const city = addressParts[1].trim();
        const stateZip = addressParts[2].trim().split(' ');
        const state = stateZip[0].trim();
        const zip = stateZip.length > 1 ? stateZip[1].trim() : '';

        this.form.patchValue({
          name: this.vendorInfo?.name || '',
          website: this.vendorInfo?.website || '',
          address: street,
          city: city,
          state: state,
          zip: zip,
          country: 'US'
        });

        if (shouldBypassToRoles) {
          this.currentStep = this.steps.CompanyTypeRegistration;
          this.disableContinue = false;
        }
        return;
      }
    }

    if (this.vendorInfo.address) {
      const vendorAddress = this.vendorInfo.address;

      const patchFormWithState = (): void => {
        let state = '';
        if ('stateId' in vendorAddress && vendorAddress.stateId) {
          const stateById = this._stateQuery.getByStateId(
            Number(vendorAddress.stateId)
          );
          state = stateById?.abbreviation || '';
        } else if ('state' in vendorAddress && vendorAddress.state) {
          const stateByName = this._stateQuery.getByNameOrAbbreviation(
            vendorAddress.state
          );
          state = stateByName?.abbreviation || vendorAddress.state;
        }

        this.form.patchValue({
          name: this.vendorInfo?.name || null,
          website: this.vendorInfo?.website || null,
          address: 'address' in vendorAddress ? vendorAddress.address : '',
          address2: 'address2' in vendorAddress ? vendorAddress.address2 : '',
          city: 'city' in vendorAddress ? vendorAddress.city : '',
          state: state || '',
          zip: 'postalCode' in vendorAddress ? vendorAddress.postalCode : '',
          county: 'county' in vendorAddress ? vendorAddress.county : '',
          country: 'country' in vendorAddress ? vendorAddress.country : 'US',
          latitude: 'latitude' in vendorAddress ? vendorAddress.latitude : null,
          longitude:
            'longitude' in vendorAddress ? vendorAddress.longitude : null
        });

        if (shouldBypassToRoles) {
          this.currentStep = this.steps.CompanyTypeRegistration;
          this.disableContinue = false;
        } else if (this.roles && this.roles.length > 0) {
          this.disableContinue = false;
        }
      };

      if (this._stateQuery.getValue()?.states) {
        patchFormWithState();
      } else {
        const subscription = this._stateQuery
          .select((state) => state.states)
          .subscribe((states) => {
            if (states) {
              patchFormWithState();
              subscription.unsubscribe();
            }
          });
      }
    }
  }

  public emitVendorStateUpdate(): void {
    this.vendorUpdated.emit({
      name: this.form.value.name || '',
      website: this.form.value.website || '',
      processes: [],
      industries: [],
      address: {
        address: this.form.value.address || '',
        address2: this.form.value.address2 || '',
        city: this.form.value.city || '',
        stateId:
          this._stateQuery.getByNameOrAbbreviation(this.form.value.state || '')
            ?.stateId || 143,
        postalCode: this.form.value.zip || '',
        county: this.form.value.county || '',
        country: this.form.value.country || '',
        unit: ''
      }
    });
  }

  public onClickBack(): void {
    if (this.currentStep === this.steps.UserProblems) {
      this.disableContinue = true;
    }
    if (this.currentStep !== this.steps.CompanyTypeRegistration) {
      this.currentStep -= 1;
    } else {
      if (this._registrationMode === RegistrationMode.Invitation) {
        this.currentStep = this.steps.CompanyDetails;
      } else {
        this.currentStep =
          this.suggestedVendors.length > 0
            ? this.steps.CompanySuggestions
            : this.steps.CompanyDetails;
      }
    }

    this.emitVendorStateUpdate();
  }

  public onContinueClick(): void {
    if (!this.vendorInfo) {
      this.vendorInfo = {};
    }

    this.saveFormDataToLocalStorage();

    this.disableContinue = true;
    switch (this.currentStep) {
      case this.steps.CompanyDetails: {
        this.emitVendorStateUpdate();

        if (this.vendorInfo) this.vendorInfo.name = this.form.value.name!;

        if (this._registrationMode === RegistrationMode.Invitation) {
          this.suggestedVendors = [];
          this.currentStep = this.steps.CompanyTypeRegistration;
          this.logVendorStep();
          this.saveFormDataToLocalStorage();
          this.loadCompaniesSuggestion = false;
          break;
        }

        const hasSustainmentId =
          this._userAccountQuery.getValue()?.sustainmentId;

        if (hasSustainmentId) {
          this.loadSuggestedCompanies();
        } else {
          this.suggestedVendors = [];
          this.currentStep = this.steps.CompanyTypeRegistration;
          this.logVendorStep();
          this.saveFormDataToLocalStorage();
          this.loadCompaniesSuggestion = false;
        }
        break;
      }
      case this.steps.CompanySuggestions:
        if (this.vendorSelected?.isRegistered) {
          this.requestedToJoin.emit(this.vendorSelected);
        } else {
          this.currentStep = this.steps.CompanyTypeRegistration;
          this.saveFormDataToLocalStorage();
        }
        break;

      case this.steps.CompanyTypeRegistration:
        if (this.userRolesLoading) {
          this.disableContinue = false;
          return;
        }

        this.selectedProblems = this.getUserRoles()
          .filter((role: UserRole) =>
            this.roles.map((e) => e.id).includes(role.userRoleId)
          )
          .reduce((acc: ManufacturingHelper.UserProblem[], role: UserRole) => {
            role.problems.forEach(
              (problem: ManufacturingHelper.UserProblem) => {
                if (
                  !acc.some((p) => p.userProblemId === problem.userProblemId)
                ) {
                  acc.push(problem);
                }
              }
            );
            return acc;
          }, [])
          .sort(
            (
              a: ManufacturingHelper.UserProblem,
              b: ManufacturingHelper.UserProblem
            ) => a.userProblemId - b.userProblemId
          );
        if (this.selectedProblems.length) {
          this.currentStep = this.steps.UserProblems;
          this.saveFormDataToLocalStorage();
        } else {
          this.saveVendor();
        }
        break;
      case this.steps.UserProblems:
        this.saveVendor();
        break;
      default:
        break;
    }
  }

  private getWebsiteDomain(website: string): string {
    if (!website) return '';

    website = website.replace(/\s+/g, '');

    if (!website.startsWith('http://') && !website.startsWith('https://')) {
      website = 'http://' + website;
    }

    const parsedUrl = new URL(website);
    return parsedUrl.host;
  }

  public enableContinue(event: SupplierListModel | boolean): void {
    this.disableContinue = !event;

    if (
      this.isInternationalCompany &&
      !this.internationalAcknowledgmentAccepted
    )
      this.disableContinue = true;

    if (typeof event !== 'boolean' && event?.sustainmentId) {
      this.vendorSelected = event;
      this.disableContinue = this.vendorSelected.isSelected ? false : true;

      if (this.vendorSelected.isSelected) {
        this._registrationMode = RegistrationMode.Claim;
      }
    }
  }

  public onNoneClicked(): void {
    this.vendorSelected = null;
    this.currentStep = this.steps.CompanyTypeRegistration;
  }

  public saveVendor(): void {
    this.disableContinue = true;

    this.emitVendorStateUpdate();

    const vendor: IVendorOnboardingResult = {
      name: this.vendorSelected?.name || this.form.value.name || '',
      primaryAddress: {
        address:
          this.vendorSelected?.address?.addressLine ||
          this.form.value.address ||
          '',
        address2:
          this.vendorSelected?.address.addressLine2 ||
          this.form.value.address2 ||
          '',
        postalCode: this.form.value.zip || '',
        state: this.form.value.state || '',
        latitude:
          this.vendorSelected?.address?.latitude ||
          this.form.value.latitude ||
          null,
        longitude:
          this.vendorSelected?.address?.longitude ||
          this.form.value.longitude ||
          null,
        city: this.vendorSelected?.address?.city || this.form.value.city || '',
        county:
          this.vendorSelected?.address?.county || this.form.value.county || '',
        country: this.form.controls.country.value || ''
      },
      logo: this.vendorSelected?.logo || null,
      website: this.vendorSelected?.website || this.form.value.website || '',
      processes: [],
      industries: [],
      sustainmentId:
        this.vendorSelected?.sustainmentId ||
        this._userAccountQuery.getValue().sustainmentId,
      isBuyer: this.getUserRoles().some(
        (role: UserRole) =>
          role.buyerRole &&
          this.roles.map((e) => e.id).includes(role.userRoleId)
      ),
      isSupplier: this.getUserRoles().some(
        (role: UserRole) =>
          role.supplierRole &&
          this.roles.map((e) => e.id).includes(role.userRoleId)
      ),
      isOther: this.getUserRoles().some(
        (role: UserRole) =>
          role.otherRole &&
          this.roles.map((e) => e.id).includes(role.userRoleId)
      )
    };

    this.saveFormDataToLocalStorage();

    this.patchVendor.emit({
      vendor,
      roles: this.roles,
      problems: this.problems
    });
  }

  public acknowledgeInternationalAccepted(event: boolean): void {
    this.internationalAcknowledgmentAccepted = event;

    if (this.form.valid) {
      this.disableContinue = !event;
    }
    this.saveFormDataToLocalStorage();
  }

  public internationalCompanyChange(event: boolean): void {
    this.isInternationalCompany = event;
    this.saveFormDataToLocalStorage();
  }

  private logVendorStep(): void {
    this.loggerDatadogService.info(
      `Onboarding step ${this.steps[this.currentStep]}`,
      { data: this.vendorInfo, action: 'onboarding' }
    );
  }

  public saveFormDataToLocalStorage(): void {
    const formData = {
      vendorInfo: {
        ...this.vendorInfo,
        name: this.form.value.name,
        website: this.form.value.website,
        address: {
          address: this.form.value.address,
          address2: this.form.value.address2,
          city: this.form.value.city,
          postalCode: this.form.value.zip,
          county: this.form.value.county,
          country: this.form.value.country,
          state: this.form.value.state,
          stateId: this._stateQuery.getByNameOrAbbreviation(
            this.form.value.state || ''
          )?.stateId,
          latitude: this.form.value.latitude,
          longitude: this.form.value.longitude
        }
      },
      currentStep: this.currentStep,
      roles: this.roles,
      problems: this.problems,
      vendorSelected: this.vendorSelected,
      formValues: this.form.value
    };
    localStorage.setItem(this._localStorageKey, JSON.stringify(formData));
  }

  public getUserRoles(): UserRole[] {
    return this.userRolesData || [];
  }

  public selectedRoles(roles: { id: number; text: string }[]): void {
    this.roles = roles;
    this._rolesSubject.next(roles);
    this.saveFormDataToLocalStorage();
  }

  public selectProblems(problems: { id: number; text: string }[]): void {
    this.problems = problems;
    this.saveFormDataToLocalStorage();
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  /**
   * Load suggested companies from the API based on current form values
   * This method is called both when initializing the component with saved state
   * and when clicking Continue from the Company Details step
   */
  public loadSuggestedCompanies(): void {
    this.loadCompaniesSuggestion = true;
    const country = this.form.controls.country.value || '';

    this.onboardingApi
      .getOpensearchResults({
        companyName: this.form.value.name || '',
        state: country == 'US' ? this.form.controls.state.value || '' : '',
        website: this.getWebsiteDomain(this.form.value.website || ''),
        country: country == 'US' ? '' : country
      })
      .pipe(map((x) => x))
      .subscribe((results) => {
        if (results) {
          if (this._registrationMode === RegistrationMode.Invitation && this.vendorInfo?.sustainmentId) {
            results = results.filter(v => v.sustainmentId !== this.vendorInfo.sustainmentId);
          }

          results.forEach((vendor) => {
            if (vendor && vendor && vendor.processes)
              vendor.processes =
                ManufacturingHelper.mapVendorProfileProcessSubprocess(
                  vendor,
                  this.processes
                );
          });

          this.suggestedVendors = results;
          this.loadCompaniesSuggestion = false;

          if (this.vendorSelected) {
            const foundVendor = this.suggestedVendors.find(
              (v) => v.sustainmentId === this.vendorSelected?.sustainmentId
            );

            if (foundVendor) {
              foundVendor.isSelected = true;
              this.vendorSelected = foundVendor;
              this.disableContinue = false;
            }
          }

          if (this.currentStep === this.steps.CompanyDetails) {
            this.currentStep =
              this.suggestedVendors.length > 0
                ? this.steps.CompanySuggestions
                : this.steps.CompanyTypeRegistration;

            this.logVendorStep();
            this.saveFormDataToLocalStorage();

            if (this.suggestedVendors.length === 0) {
              this.onNoneClicked();
            }
          }
        }
      });
  }
}
